import React from 'react';
import Modal from '../ui/Modal';
import { RiAlertLine, RiCloseLine, RiArrowRightLine, RiFileTextLine, RiCloseCircleLine } from 'react-icons/ri';

interface Rental {
  id: number;
  code: string;
  company_name?: string;
  site_name?: string;
  status: string;
}

interface RentalCancellationFlowModalProps {
  isOpen: boolean;
  onClose: () => void;
  rental: Rental | null;
  onSelectNotification: () => void;  // แจ้งความประสงค์ → navigate
  onSelectImmediate: () => void;     // ยกเลิกทันที → confirm dialog
}

const RentalCancellationFlowModal: React.FC<RentalCancellationFlowModalProps> = ({
  isOpen,
  onClose,
  rental,
  onSelectNotification,
  onSelectImmediate,
}) => {
  if (!rental) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="ดำเนินการยกเลิกการเช่า"
      size="lg"
    >
      {/* Rental Info */}
      <div className="mb-6 p-4 bg-secondary-50 rounded-lg">
        <div className="flex items-center gap-2 pb-2 border-b border-secondary-200 mb-3">
          <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center shadow-sm">
            <RiFileTextLine className="w-4 h-4 text-white" />
          </div>
          <h3 className="text-base font-semibold text-secondary-800">ข้อมูลการเช่า</h3>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="text-sm text-secondary-600">
            <span className="font-medium">รหัสการเช่า:</span>{' '}
            <span className="font-semibold text-secondary-900">{rental.code}</span>
          </div>
          {rental.company_name && (
            <>
              <span className="hidden sm:inline text-secondary-300">|</span>
              <div className="text-sm text-secondary-600">
                <span className="font-medium">บริษัท:</span>{' '}
                <span className="text-secondary-900">{rental.company_name}</span>
              </div>
            </>
          )}
        </div>
        {rental.site_name && (
          <div className="text-sm text-secondary-500 mt-1">
            <span className="font-medium">โครงการ:</span> {rental.site_name}
          </div>
        )}
      </div>

      {/* Options */}
      <div className="space-y-4">
        <p className="text-sm font-medium text-gray-700">เลือกประเภทการยกเลิก:</p>

        {/* Option 1: Notification Flow (Recommended) */}
        <button
          type="button"
          onClick={onSelectNotification}
          className="w-full p-4 border-2 border-orange-200 rounded-lg text-left hover:border-orange-400 hover:bg-orange-50 transition-all group focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 p-2 bg-orange-100 rounded-lg">
              <RiAlertLine className="w-5 h-5 text-orange-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-gray-900">แจ้งความประสงค์ยกเลิก</span>
                <span className="px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded-full font-medium">
                  แนะนำ
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                ลูกค้าแจ้งล่วงหน้า → ติดตาม → คืนอุปกรณ์ → ปิดสัญญา
              </p>
              <p className="text-xs text-gray-400 mt-2">
                เหมาะสำหรับ: ยกเลิกตามขั้นตอนปกติ
              </p>
            </div>
            <div className="flex-shrink-0">
              <RiArrowRightLine className="w-5 h-5 text-gray-400 group-hover:text-orange-500 transition-colors" />
            </div>
          </div>
        </button>

        {/* Option 2: Immediate Cancel */}
        <button
          type="button"
          onClick={onSelectImmediate}
          className="w-full p-4 border-2 border-red-200 rounded-lg text-left hover:border-red-400 hover:bg-red-50 transition-all group focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 p-2 bg-red-100 rounded-lg">
              <RiCloseLine className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-gray-900">ยกเลิกทันที</div>
              <p className="text-sm text-gray-500 mt-1">
                เปลี่ยนสถานะเป็น "ยกเลิก" ทันที (ไม่ผ่านขั้นตอน)
              </p>
              <p className="text-xs text-gray-400 mt-2">
                เหมาะสำหรับ: แก้ไขข้อมูลผิดพลาด / Admin override
              </p>
            </div>
            <div className="flex-shrink-0">
              <RiArrowRightLine className="w-5 h-5 text-gray-400 group-hover:text-red-500 transition-colors" />
            </div>
          </div>
        </button>
      </div>

      {/* Footer */}
      <div className="mt-6 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
        >
          ปิด
        </button>
      </div>
    </Modal>
  );
};

export default RentalCancellationFlowModal;
